import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { DatabaseBackup, Download, Loader2 } from "lucide-react";

export default function AdminBackup() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/backup/summary");
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load backup summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const download = async () => {
    setDownloading(true);
    try {
      // Stream the ZIP back with the logged-in user's token; save from Content-Disposition filename.
      const res = await api.get("/admin/backup/download", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const fname = (m && m[1]) || `Coke9_Backup_${new Date().toISOString().replace(/[:T]/g, "-").slice(0, 16)}.zip`;
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fname}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backup download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader title="Data Backup" subtitle="Full read-only export of every collection" />
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <Card className="rounded-xl border-gray-200 bg-white p-5" data-testid="backup-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                <DatabaseBackup className="h-5 w-5 text-brand-red" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">One-click database backup</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {summary
                    ? `${summary.collections.length} collections · ${summary.total_rows.toLocaleString()} rows · generated at ${summary.generated_at}`
                    : "Loading…"}
                </div>
              </div>
            </div>
            <Button
              data-testid="backup-download-btn"
              onClick={download}
              disabled={downloading || loading}
              className="bg-brand-red hover:bg-[#B31721] text-white"
            >
              {downloading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Preparing ZIP…</>
              ) : (
                <><Download className="h-4 w-4 mr-2" />Download Full Backup</>
              )}
            </Button>
          </div>
          <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
            This file contains ALL data, including personal and salary details. Store it safely and never share it.
          </div>
        </Card>

        <Card className="rounded-xl border-gray-200 bg-white overflow-hidden" data-testid="backup-summary-card">
          <div className="px-4 py-3 border-b border-gray-100 text-xs font-bold uppercase tracking-[0.14em] text-gray-500">
            Collections
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="backup-summary-table">
              <thead className="bg-gray-50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2">Collection</th>
                  <th className="px-4 py-2 text-right">Rows</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={2} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>
                )}
                {!loading && summary?.collections?.map((c) => (
                  <tr key={c.name} className="border-t border-gray-100" data-testid={`backup-row-${c.name}`}>
                    <td className="px-4 py-2 font-mono text-xs">{c.name}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{c.rows.toLocaleString()}</td>
                  </tr>
                ))}
                {!loading && summary && (
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td className="px-4 py-2 font-semibold">Total</td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums" data-testid="backup-total-rows">
                      {summary.total_rows.toLocaleString()}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
    </div>
  );
}
